/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Collapsible FAQ accordion list with single-open behavior and grid-rows animation
 *   PROPS:   items (FaqItem[]) — array of question/answer pairs, className
 *   STATE:   openIndex — tracks which item is currently expanded (null = none)
 *   DEPENDS: M-CN (cn utility), @phosphor-icons/react (CaretDown)
 *   EMITS:   none
 *   ACCESSIBILITY: buttons with text labels, keyboard navigable, animated expand/collapse
 *   LINKS:   M-FAQ
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-12] Added GRACE MODULE_CONTRACT and semantic blocks — no behavior change
 */

"use client"

import { useState } from "react"
import { CaretDown } from "@phosphor-icons/react"
import { cn } from "@/lib/cn"

// --- TypeDefinitions ---
interface FaqItem {
  question: string
  answer: string
}

interface FaqAccordionProps {
  items: FaqItem[]
  className?: string
}

// --- FaqAccordionComponent ---
export function FaqAccordion({ items, className }: FaqAccordionProps) {
  // --- AccordionState ---
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  // --- RenderItems ---
  return (
    <div className={cn("space-y-3", className)}>
      {items.map((item, i) => (
        <div
          key={i}
          className="rounded-xl border border-hairline-dark bg-surface-card-dark overflow-hidden"
        >
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="flex w-full items-center justify-between px-6 py-5 text-left transition-colors duration-150 hover:bg-surface-elevated-dark"
          >
            <span className="text-sm font-semibold text-on-dark">{item.question}</span>
            <CaretDown
              weight="regular"
              className={cn(
                "h-5 w-5 shrink-0 text-muted transition-transform duration-200",
                openIndex === i && "rotate-180",
              )}
            />
          </button>
          <div
            className={cn(
              "grid transition-all duration-200",
              openIndex === i ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
            )}
          >
            <div className="overflow-hidden">
              <p className="px-6 pb-5 text-sm leading-relaxed text-muted">{item.answer}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
